/** Mirrors PHI read path: explicit clinical, nested row, then legacy encounter column. */
export type EncounterClinicalStatusSource = {
  clinicalStatus?: string | null;
  billingStatus?: string | null;
  signReqStatus?: string | null;
  encounterStatus?: string | null;
  encounterStatusRow?: {
    clinicalStatus?: string | null;
    billingStatus?: string | null;
    signReqStatus?: string | null;
  } | null;
};

/** `encounter_status.billingStatus` / `signReqStatus` when sent on list-clinic payloads. */
export function readBillingStatusField(
  source: EncounterClinicalStatusSource | null | undefined
): string {
  if (!source) {
    return "";
  }
  const top = (source.billingStatus ?? "").toString().trim();
  if (top) {
    return top;
  }
  return (source.encounterStatusRow?.billingStatus ?? "").toString().trim();
}

export function readSignReqStatusField(
  source: EncounterClinicalStatusSource | null | undefined
): string {
  if (!source) {
    return "";
  }
  const top = (source.signReqStatus ?? "").toString().trim();
  if (top) {
    return top;
  }
  return (source.encounterStatusRow?.signReqStatus ?? "").toString().trim();
}

/** `encounter_status.clinicalStatus` when present on the payload. */
export function readClinicalStatusField(
  source: EncounterClinicalStatusSource | null | undefined
): string {
  if (!source) {
    return "";
  }
  const top = (source.clinicalStatus ?? "").toString().trim();
  if (top) {
    return top;
  }
  const fromRow = (source.encounterStatusRow?.clinicalStatus ?? "")
    .toString()
    .trim();
  if (fromRow) {
    return fromRow;
  }
  const record = source as Record<string, unknown>;
  const nestedRow = record["encounter_status"];
  if (nestedRow && typeof nestedRow === "object" && !Array.isArray(nestedRow)) {
    const row = nestedRow as Record<string, unknown>;
    const nestedClinical = String(
      row["clinicalStatus"] ?? row["clinical_status"] ?? ""
    ).trim();
    if (nestedClinical) {
      return nestedClinical;
    }
  }
  const encounterStatus = record["encounterStatus"];
  if (
    encounterStatus &&
    typeof encounterStatus === "object" &&
    !Array.isArray(encounterStatus)
  ) {
    const row = encounterStatus as Record<string, unknown>;
    return String(row["clinicalStatus"] ?? row["clinical_status"] ?? "").trim();
  }
  return "";
}

/** Legacy `encounter.encounterStatus` column (may differ from clinical when both are sent). */
export function readLegacyEncounterStatusField(
  source: EncounterClinicalStatusSource | null | undefined
): string {
  if (!source) {
    return "";
  }
  return (source.encounterStatus ?? "").toString().trim();
}
